import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectProps } from '@amzn/awsui-components-core';
import {
  ContentPriority,
  ContentQualityHyperlinks,
  ContentQualityQuestionData,
  ContentQualityResponseOptions,
} from '@amzn/awscat-pattern-library-service-model';

import { useBarRaiserEvaluationService } from '../../contexts/BarRaiserEvaluationServiceContext';
import { useCanActAsBarRaiser } from '../v2-hooks/useCanActAsBarRaiser';
import { useLoadedContentId } from '../v2-hooks/useLoadedContentId';
import { BarRaiserEvaluationService } from '../../services/BarRaiserEvaluationService';
import { ACSII_VALUE_FOR_A, COMMENT_MAX, isScoreValid } from '../utils/ContentQualityUtils';
import { responseOptionsDisplayNames } from '../utils/DisplayNames';
import { getResponseOptions } from '../utils/SelectOptions';
import { ContentPriorityOptions } from '../utils/SelectableOptions';

/** Caption shown beneath the score gauge. */
const QUALITY_SCORE_CAPTION = 'Technical quality score';

/** Presentation model for one questionnaire row. */
export interface BarRaiserQuestionItemViewModel {
  readonly sequence: number;
  /** Display prefix: top-level questions show "1.", sub-questions show "a.". */
  readonly displaySequence: string;
  readonly questionBody: string;
  readonly hyperlinks: ContentQualityHyperlinks | undefined;
  readonly note: string | undefined;
  readonly responseOptions: readonly SelectProps.Option[];
  readonly selectedOption: SelectProps.Option | null;
  readonly comment: string;
  /** The comment box is only shown when the answer is "No". */
  readonly showCommentBox: boolean;
  /** When true, the answer and comment are locked — a non-bar-raiser is viewing feedback. */
  readonly isReadOnly: boolean;
  /** The comment box stops accepting text at this many characters. */
  readonly commentMaxLength: number;
  readonly commentCharsRemaining: number;
  readonly onResponseChange: (response: ContentQualityResponseOptions) => void;
  readonly onCommentChange: (comment: string) => void;
}

/** A category heading with its visible questions, in display order. */
export interface BarRaiserCategoryViewModel {
  readonly categoryName: string;
  readonly questions: readonly BarRaiserQuestionItemViewModel[];
}

export type BarRaiserDecision = 'publish' | 'reject';

export type SubmitScreen =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting'; readonly decision: BarRaiserDecision }
  | { readonly kind: 'succeeded'; readonly decision: BarRaiserDecision }
  | { readonly kind: 'failed'; readonly decision: BarRaiserDecision };

export interface BarRaiserEvaluationPanelViewModel {
  readonly isLoading: boolean;
  readonly hasError: boolean;
  readonly errorMessage: string | undefined;
  readonly dismissError: () => void;

  readonly scoreForGauge: number;
  /** False until every visible question is answered, so the gauge can show a placeholder. */
  readonly isScoreComplete: boolean;
  readonly qualityScoreCaption: string;

  readonly categories: readonly BarRaiserCategoryViewModel[];

  /** False for a non-bar-raiser viewing feedback: the publish/reject buttons are hidden. */
  readonly showDecisionButtons: boolean;
  readonly isPublishDisabled: boolean;
  readonly isRejectDisabled: boolean;
  /** Approves directly for internal content, or opens the release-priority modal for external content. */
  readonly publish: () => void;
  readonly reject: () => void;

  /** Which overlay the panel shows after a submit: idle (form), submitting (spinner), succeeded, or failed. */
  readonly submitScreen: SubmitScreen;

  // Release-priority modal (external-audience approvals only).
  readonly isPriorityModalVisible: boolean;
  readonly priorityOptions: readonly SelectProps.Option[];
  readonly selectedPriorityOption: SelectProps.Option | null;
  readonly isPriorityConfirmDisabled: boolean;
  readonly setSelectedPriority: (option: SelectProps.Option) => void;
  readonly dismissPriorityModal: () => void;
  readonly confirmPublishWithPriority: () => void;
}

interface ServiceSnapshot {
  readonly loadState: BarRaiserEvaluationService['loadState'];
  readonly submitState: BarRaiserEvaluationService['submitState'];
  readonly errorMessage: string | undefined;
  readonly score: number;
  readonly version: number;
}

/** Subscribes to `service.changed` and returns a fresh snapshot on each emit. */
function useServiceSnapshot(service: BarRaiserEvaluationService): ServiceSnapshot {
  const read = useCallback(
    (version: number): ServiceSnapshot => ({
      loadState: service.loadState,
      submitState: service.submitState,
      errorMessage: service.errorMessage,
      score: service.score,
      version,
    }),
    [service],
  );

  // Monotonic across the hook's lifetime, so a service swap never reuses a number.
  const versionRef = useRef(0);
  const [snapshot, setSnapshot] = useState<ServiceSnapshot>(() => read(versionRef.current));

  useEffect(() => {
    let isMounted = true;
    // When the service changes, re-read the snapshot (version bump forces a render
    // even when only the question maps — not the scalar fields — changed).
    setSnapshot(read(++versionRef.current));
    const unsubscribe = service.on('changed', () => {
      if (isMounted) setSnapshot(read(++versionRef.current));
    });
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [service, read]);

  return snapshot;
}

/** Display prefix for a question sequence: "1." for top-level, "a." for sub-questions. */
function toDisplaySequence(questionSequence: string): string {
  if (questionSequence.includes('.')) {
    const subIndex = Number(questionSequence.split('.')[1]);
    return `${String.fromCharCode(ACSII_VALUE_FOR_A + subIndex - 1)}.`;
  }
  return `${questionSequence}.`;
}

/**
 * Binds the bar-raiser evaluation panel to its service: loads the questionnaire
 * for the current content, exposes the live score and grouped questions, and
 * submits the publish/reject decision (navigating to the viewer on success).
 */
export function useBarRaiserEvaluationPanelViewModel(): BarRaiserEvaluationPanelViewModel {
  const service = useBarRaiserEvaluationService();
  const contentId = useLoadedContentId();
  const snapshot = useServiceSnapshot(service);
  const canActAsBarRaiser = useCanActAsBarRaiser();

  // Only a bar raiser may edit and submit; every other role views the submitted feedback read-only.
  const isReadOnly = !canActAsBarRaiser;

  // When the panel mounts (or the content changes), load that content's evaluation.
  useEffect(() => {
    if (contentId) service.load(contentId);
  }, [service, contentId]);

  const dismissError = useCallback(() => service.clearError(), [service]);

  // Build the grouped, ordered question list from the service maps.
  const categories = useMemo<readonly BarRaiserCategoryViewModel[]>(() => {
    return service.categories.map((category) => {
      const sequences = service.categoryToSequences.get(category.category) ?? [];
      const questions = sequences
        // Resolve each visible sequence to its question, dropping any whose maps
        // have drifted apart rather than throwing on a missing entry.
        .filter((sequence) => service.visibility.get(sequence))
        .map((sequence) => ({ sequence, question: service.questionsBySequence.get(sequence) }))
        .filter((entry) => entry.question !== undefined)
        .map(({ sequence, question }) =>
          buildQuestionItem(service, sequence, question!, isReadOnly),
        );
      return { categoryName: category.category, questions };
    });
    // The maps are replaced atomically per `changed`, so version drives recompute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, snapshot.version, isReadOnly]);

  // Derive submit affordances from the live score and busy state.
  const submitScreen = useMemo<SubmitScreen>(() => {
    switch (snapshot.submitState) {
      case 'publishing':
        return { kind: 'submitting', decision: 'publish' };
      case 'rejecting':
        return { kind: 'submitting', decision: 'reject' };
      case 'publish_succeeded':
        return { kind: 'succeeded', decision: 'publish' };
      case 'reject_succeeded':
        return { kind: 'succeeded', decision: 'reject' };
      case 'publish_failed':
        return { kind: 'failed', decision: 'publish' };
      case 'reject_failed':
        return { kind: 'failed', decision: 'reject' };
      default:
        return { kind: 'idle' };
    }
  }, [snapshot.submitState]);
  const isBusy = snapshot.submitState !== 'idle';

  // Release-priority modal state, used only for external-audience approvals.
  const [isPriorityModalVisible, setIsPriorityModalVisible] = useState(false);
  const [selectedPriorityOption, setSelectedPriorityOption] = useState<SelectProps.Option | null>(
    null,
  );

  // External content needs a release priority, so open the modal instead of approving directly.
  const publish = useCallback(() => {
    if (service.requiresReleasePriority) {
      setIsPriorityModalVisible(true);
      return;
    }
    void service.publish();
  }, [service]);

  const dismissPriorityModal = useCallback(() => setIsPriorityModalVisible(false), []);

  const setSelectedPriority = useCallback(
    (option: SelectProps.Option) => setSelectedPriorityOption(option),
    [],
  );

  // When the bar raiser confirms a priority, close the modal right away (publish resolves
  // only after polling) and approve; the panel then shows the in-progress/outcome screen.
  const confirmPublishWithPriority = useCallback(() => {
    const priority = selectedPriorityOption
      ? (Number(selectedPriorityOption.value) as ContentPriority)
      : undefined;
    setIsPriorityModalVisible(false);
    void service.publish(priority);
  }, [service, selectedPriorityOption]);

  // When the bar raiser rejects the content, ask the service to reject
  const reject = useCallback(() => void service.reject(), [service]);

  return {
    isLoading: snapshot.loadState === 'loading' || snapshot.loadState === 'idle',
    hasError: snapshot.loadState === 'error',
    errorMessage: snapshot.errorMessage,
    dismissError,

    scoreForGauge: snapshot.score,
    isScoreComplete: snapshot.score >= 0,
    qualityScoreCaption: QUALITY_SCORE_CAPTION,

    categories,

    showDecisionButtons: !isReadOnly,
    submitScreen,
    isPublishDisabled: isBusy || !isScoreValid(snapshot.score),
    isRejectDisabled: isBusy || !isScoreValid(snapshot.score),
    publish,
    reject,
    isPriorityModalVisible,
    priorityOptions: ContentPriorityOptions,
    selectedPriorityOption,
    isPriorityConfirmDisabled: isBusy || selectedPriorityOption === null,
    setSelectedPriority,
    dismissPriorityModal,
    confirmPublishWithPriority,
  };
}

/** Assemble the per-question presentation model from the service state. */
function buildQuestionItem(
  service: BarRaiserEvaluationService,
  sequence: number,
  question: ContentQualityQuestionData,
  isReadOnly: boolean,
): BarRaiserQuestionItemViewModel {
  const stored = service.responses.get(sequence);
  const response = stored?.response ?? '';
  const comment = stored?.comment ?? '';

  // Limit the dropdown to the answers this question allows.
  const excluded = Object.keys(ContentQualityResponseOptions).filter(
    (option) => !question.responseOptions.has(option as ContentQualityResponseOptions),
  );
  const responseOptions = getResponseOptions(excluded);
  const selectedOption = response
    ? {
        label:
          responseOptionsDisplayNames.get(response as ContentQualityResponseOptions) ?? response,
        value: response,
      }
    : null;

  return {
    sequence,
    displaySequence: toDisplaySequence(question.questionSequence),
    questionBody: question.questionBody,
    hyperlinks: question.hyperlinks,
    note: question.note || undefined,
    responseOptions,
    selectedOption,
    comment,
    showCommentBox: response === ContentQualityResponseOptions.NO,
    isReadOnly,
    commentMaxLength: COMMENT_MAX,
    commentCharsRemaining: COMMENT_MAX - comment.length,
    onResponseChange: (newResponse) => service.setResponse(sequence, newResponse, comment),
    onCommentChange: (newComment) =>
      service.setResponse(sequence, response as ContentQualityResponseOptions, newComment),
  };
}
